/**
 * @module components/ui/NovaIconButton
 * Figma-aligned icon-only control powered by dedicated `iconButton` recipe.
 *
 * - **secondary** / **primary** — Figma S / M / L; **`size` `s` | `m` | `l`** required.
 * - **tertiaryCircular** — Figma single size; **no `size` prop** (recipe `m` + `l` glyph).
 */

import { forwardRef, type ComponentProps, type ElementType, type ReactNode } from 'react';
import { Spinner, chakra, type HTMLChakraProps, useRecipe } from '@chakra-ui/react';

import { NovaIcon, type NovaIconGlyphProps } from 'components/ui/novaIcon';

/** Figma S / M / L — **secondary** and **primary**. */
export type NovaIconButtonStandardSize = 's' | 'm' | 'l';

/** Figma-facing variant names (map to `iconButton` recipe `variant` values). */
export type NovaIconButtonVariant = 'secondary' | 'primary' | 'tertiaryCircular';

/** Props forwarded to the inner {@link NovaIcon} (not `as` / `icon` / `size`). */
export type NovaIconButtonIconProps = Omit<ComponentProps<typeof NovaIcon>, 'as' | 'icon' | 'size'>;

type NovaIconButtonPassthrough = Omit<HTMLChakraProps<'button'>, 'children'> & {
  loading?: boolean;
};

type NovaIconButtonGlyphSlot =
  | {
      icon: ElementType<NovaIconGlyphProps>;
      iconProps?: NovaIconButtonIconProps;
      children?: never;
    }
  | {
      children: ReactNode;
      icon?: never;
      iconProps?: never;
    };

export type NovaIconButtonBaseProps = NovaIconButtonPassthrough & NovaIconButtonGlyphSlot;

/** Secondary / Default — bordered square; recipe `secondary` + `s` | `m` | `l`. */
export type NovaIconButtonSecondaryStandardProps = NovaIconButtonBaseProps & {
  variant: 'secondary';
  size: NovaIconButtonStandardSize;
};

/** Primary / Standard — recipe `primary` + `s` | `m` | `l`. */
export type NovaIconButtonPrimaryStandardProps = NovaIconButtonBaseProps & {
  variant: 'primary';
  size: NovaIconButtonStandardSize;
};

/** Tertiary / Circular — recipe `tertiaryCircular` + `l` (implicit **L** glyph). */
export type NovaIconButtonTertiaryCircularProps = NovaIconButtonBaseProps & {
  variant: 'tertiaryCircular';
  size?: never;
};

export type NovaIconButtonProps =
  | NovaIconButtonSecondaryStandardProps
  | NovaIconButtonPrimaryStandardProps
  | NovaIconButtonTertiaryCircularProps;

const GLYPH_TO_SPINNER_SIZE: Record<NovaIconButtonStandardSize, 'xs' | 'sm' | 'md'> = {
  s: 'xs',
  m: 'sm',
  l: 'md',
};

/**
 * Icon-only control: **`iconButton`** recipe + inner {@link NovaIcon}.
 *
 * - Pass **plain icon components from `assets/icons`** via `icon={...}` (do not pass a `<NovaIcon />` yourself).
 * - For standard variants, use **`variant="primary"`** or **`variant="secondary"`** with
 *   **`size="s"|"m"|"l"`**.
 * - For **`variant="tertiaryCircular"`**, omit `size` (the component maps it to recipe size `m`
 *   with glyph size `l`).
 * - Use **`iconProps`** to tweak icon color/transform when needed.
 * - Use **`children`** only as an escape hatch when you cannot provide `icon`.
 * - Always set an accessible name (`aria-label` or equivalent labelling props), because this is
 *   usually an icon-only button.
 *
 * @example
 * ```tsx
 * import { FilterIcon, SidebarIcon } from 'assets/icons/v2';
 *
 * <NovaIconButton variant="primary" size="m" icon={SidebarIcon} aria-label="Menü" />
 * <NovaIconButton variant="secondary" size="m" icon={FilterIcon} aria-label="Filtern" />
 * <NovaIconButton variant="tertiaryCircular" icon={SidebarIcon} aria-label="Zurück" />
 * ```
 */
export const NovaIconButton = forwardRef<HTMLButtonElement, NovaIconButtonProps>((props, ref) => {
  const iconButtonRecipe = useRecipe({ key: 'iconButton' });
  const { icon, iconProps, children, loading, disabled, ...rest } = props;

  const recipeVariant: NovaIconButtonVariant =
    props.variant === 'tertiaryCircular' ? 'tertiaryCircular' : props.variant;
  const recipeSize: NovaIconButtonStandardSize =
    props.variant === 'tertiaryCircular' ? 'm' : props.size;
  const glyphSize: NovaIconButtonStandardSize =
    props.variant === 'tertiaryCircular' ? 'l' : props.size;

  const content =
    icon != null ? <NovaIcon icon={icon} size={glyphSize} aria-hidden {...iconProps} /> : children;

  if (content == null) {
    throw new Error('NovaIconButton: pass `icon` or `children`');
  }

  const isDisabled = disabled === true || loading === true;
  const recipeStyles = iconButtonRecipe({
    variant: recipeVariant,
    size: recipeSize,
  });

  return (
    <chakra.button
      ref={ref}
      type="button"
      disabled={isDisabled}
      aria-busy={loading === true ? 'true' : undefined}
      data-loading={loading === true ? '' : undefined}
      css={recipeStyles}
      {...rest}
    >
      {loading === true ? <Spinner size={GLYPH_TO_SPINNER_SIZE[glyphSize]} /> : content}
    </chakra.button>
  );
});

NovaIconButton.displayName = 'NovaIconButton';
